import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/dataProvider/axios';
import { captureOrgToken, isOrgTokenCurrent } from '@/lib/org-scope';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import {
  ORG_BILLING_CANCEL_ENDPOINT,
  ORG_BILLING_CHANGE_PLAN_ENDPOINT,
  ORG_BILLING_CHECKOUT_ENDPOINT,
  ORG_BILLING_CREDITS_ASSIGN_ENDPOINT,
  ORG_BILLING_CREDITS_ENDPOINT,
  ORG_BILLING_CREDITS_PURCHASE_ENDPOINT,
  ORG_BILLING_ENDPOINT,
  ORG_BILLING_INVOICES_ENDPOINT,
  ORG_BILLING_PORTAL_ENDPOINT,
  ORG_BILLING_RESUME_ENDPOINT,
  ORG_BILLING_TOPUP_BULK_ENDPOINT,
  ORG_BILLING_TOPUP_ENDPOINT,
} from '@/services/org-admin';
import type {
  AssignCreditsInput,
  AssignCreditsResponse,
  BulkTopUpInput,
  BulkTopUpResponse,
  CancelSubscriptionResponse,
  ChangePlanInput,
  ChangePlanResponse,
  CheckoutResponse,
  CreateCheckoutInput,
  OrgBillingStatus,
  OrgCreditsResponse,
  PurchaseCreditsInput,
  PurchaseCreditsResponse,
  OrgInvoiceListResponse,
  PortalResponse,
  TopUpInput,
  TopUpResponse,
} from '@/services/org-admin';

/**
 * Billing data layer. React Query owns all of it — there is no billing store.
 *
 * Nothing here needs to outlive the panel or be read from outside it: the org billing status is
 * server state (React Query), which dialog is open is component state, and "a purchase is
 * processing" is a fact about the current page view, not about the application. A Zustand store
 * would only be a second copy of the query cache with its own staleness bugs.
 *
 * ORG ISOLATION — two mechanisms, both pre-existing:
 *
 *   1. `orgId` is in the query key, so `connectOrgScopeToQueryCache` (lib/org-scope/bridge.ts)
 *      REMOVES this org's entry the moment the org changes. Org B can neither read nor inherit
 *      Org A's billing status.
 *   2. Mutations capture an `OrgToken` when they fire and check it before touching the cache. A
 *      checkout or top-up that resolves after the user has switched orgs must not invalidate — or
 *      worse, write into — the new org's billing. The token carries an epoch as well as an id,
 *      which is what makes A → B → A safe: same `orgId`, different epoch, still stale.
 */

const billingKey = (orgId: string | null) => ['org-billing', orgId] as const;
const invoicesKey = (orgId: string | null) => ['org-billing-invoices', orgId] as const;
const creditsKey = (orgId: string | null) => ['org-billing-credits', orgId] as const;

/**
 * Axios request config for every billing call.
 *
 * `silent` here is NOT the same flag as react-query's `meta: { silent: true }`, which suppresses the
 * app's toast. This one is read by the response interceptor in dataProvider/axios, which otherwise
 * `console.error`s the full error response body in local builds. On these five endpoints that body
 * describes a payment, so it is exactly what must not end up in a console, a screen recording or a
 * pasted stack trace. Both flags are set deliberately; neither substitutes for the other.
 */
const BILLING_REQUEST = { silent: true } as const;

/**
 * A fresh idempotency key for one user action.
 *
 * Sent as `Idempotency-Key`, which Stripe uses to replay the original response for a repeat within
 * 24 hours instead of charging again. The key must be generated ONCE per user action and reused by
 * every retry of it — a key made per HTTP attempt guards nothing, which is exactly what
 * `stripity_stripe`'s own auto-generated key does server-side.
 *
 * `crypto.randomUUID` is unavailable on insecure origins in some browsers, hence the fallback.
 */
function idempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Request config for a call that moves money, carrying a fresh idempotency key.
 *
 * One key per `mutationFn` invocation, which equals one per user action here because every paying
 * mutation in this file sets `retry: false` — react-query never re-invokes it behind our back. What
 * the key buys is protection at the layer below: a transport-level retry, or a duplicate that
 * reaches Stripe twice, is collapsed into the original charge rather than becoming a second one.
 */
function payingRequest() {
  return { ...BILLING_REQUEST, headers: { 'Idempotency-Key': idempotencyKey() } };
}

/**
 * Org billing status.
 *
 * @param options.refetchInterval Set while a Stripe operation is settling. Polling is driven by the
 * panel rather than being always-on: the webhook is the only thing that changes this data, so
 * polling when nothing is in flight is pure request noise.
 */
export function useOrgBilling(options?: { enabled?: boolean; refetchInterval?: number | false }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useQuery({
    queryKey: billingKey(orgId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<OrgBillingStatus>(ORG_BILLING_ENDPOINT(orgId!), BILLING_REQUEST);
      return data;
    },
    enabled: !!orgId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? false,
    // Billing is money state an admin has usually just changed elsewhere (Stripe portal, a
    // colleague's purchase), so a visit re-reads rather than trusting a cached snapshot.
    refetchOnMount: 'always',
  });
}

/** Invalidate billing — and usage, when a mutation moves a budget. */
function useBillingInvalidator() {
  const queryClient = useQueryClient();
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return (token: ReturnType<typeof captureOrgToken>, alsoUsage = false) => {
    // The guard is the point: without it, a response arriving after an org switch would refetch
    // billing for whichever org is now on screen, using a request that was authorised for another.
    if (!isOrgTokenCurrent(token)) return;
    void queryClient.invalidateQueries({ queryKey: billingKey(orgId) });
    if (alsoUsage) {
      void queryClient.invalidateQueries({ queryKey: ['org-usage', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['org-usage-report', orgId] });
    }
    // Anything that moves money produces an invoice — a prorated plan change produces one
    // immediately, which is the whole point of `always_invoice`. Leaving the list stale means the
    // charge the admin just made is the one invoice they cannot see.
    void queryClient.invalidateQueries({ queryKey: invoicesKey(orgId) });
    // The pool moves on both a purchase and an assignment, and an assignment also moves budgets —
    // so it is invalidated on every money mutation rather than only the two that name it.
    void queryClient.invalidateQueries({ queryKey: creditsKey(orgId) });
  };
}

/**
 * Start a Stripe Checkout session for a member's plan.
 *
 * Returns a URL and nothing else — the plan and budget land later, when Stripe's webhook fires.
 * Callers redirect; they must not treat this resolving as a completed purchase.
 */
export function useCreateCheckout() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useMutation({
    mutationFn: async (input: CreateCheckoutInput) => {
      const { data } = await axiosInstance.post<CheckoutResponse>(
        ORG_BILLING_CHECKOUT_ENDPOINT(orgId!),
        input,
        BILLING_REQUEST,
      );
      return data;
    },
    // No cache write: nothing has changed server-side yet, and the browser is about to leave.
    retry: false,
    meta: { silent: true },
  });
}

/**
 * Top up a member's budget.
 *
 * @deprecated Backend #327: the card top-up endpoints are slated for removal and their amounts are
 * still wiped at cycle reset. No panel surface mounts this; money reaches a seat via
 * `usePurchaseCredits` + `useAssignCredits`, which carries over. Delete with the endpoint.
 *
 * `retry: false` is a correctness requirement, not a preference. docs/billing-api.md records that
 * this endpoint sends no Stripe idempotency key, so a retried request has a window in which it can
 * charge the card twice. React Query's default retry would do exactly that on a flaky connection.
 */
export function useTopUp() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const invalidate = useBillingInvalidator();

  return useMutation({
    // The org token rides in mutation context rather than the return value, so callers receive a
    // plain `TopUpResponse` and still get the stale-write guard.
    onMutate: () => captureOrgToken(),
    mutationFn: async (input: TopUpInput) => {
      const { data } = await axiosInstance.post<TopUpResponse>(
        ORG_BILLING_TOPUP_ENDPOINT(orgId!),
        input,
        BILLING_REQUEST,
      );
      return data;
    },
    onSuccess: (data, _input, token) => {
      // Only the off-session charge changes anything now; the Checkout branch changes nothing until
      // the browser has been to Stripe and back.
      if ('status' in data) invalidate(token, true);
    },
    retry: false,
    meta: { silent: true },
  });
}

/** Stripe Customer Portal URL for card and invoice management. */
export function useBillingPortal() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post<PortalResponse>(
        ORG_BILLING_PORTAL_ENDPOINT(orgId!),
        undefined,
        BILLING_REQUEST,
      );
      return data;
    },
    retry: false,
    meta: { silent: true },
  });
}

/**
 * Cancel a member's recurring subscription at period end.
 *
 * Not an instant cancellation: the seat keeps its budget until the period actually ends, when
 * Stripe's `customer.subscription.deleted` webhook downgrades it to free. The response's `status`
 * is Stripe's status at the moment of the call and is typically still `"active"` — which is why the
 * UI must not report the subscription as cancelled.
 */
export function useCancelSubscription() {
  return useSubscriptionMutation<CancelSubscriptionResponse>(ORG_BILLING_CANCEL_ENDPOINT);
}

/**
 * Undo a scheduled cancellation — the subscription renews at period end as normal.
 *
 * Unlike cancel, the backend stamps `cancel_at_period_end: false` itself before answering, so the
 * refetch this triggers already shows the seat as renewing.
 */
export function useResumeSubscription() {
  return useSubscriptionMutation<{ cancel_at_period_end: false }>(ORG_BILLING_RESUME_ENDPOINT);
}

/** A per-seat subscription POST. Shared by cancel and resume, which differ only by endpoint. */
function useSubscriptionMutation<TResponse>(
  endpoint: (organizationId: string, userId: string) => string,
) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const invalidate = useBillingInvalidator();

  return useMutation({
    onMutate: () => captureOrgToken(),
    mutationFn: async (userId: string) => {
      const { data } = await axiosInstance.post<TResponse>(
        endpoint(orgId!, userId),
        undefined,
        BILLING_REQUEST,
      );
      return data;
    },
    onSuccess: (_data, _userId, token) => invalidate(token),
    retry: false,
    meta: { silent: true },
  });
}

/**
 * The org's Stripe invoices.
 *
 * Separate query from `useOrgBilling` rather than a field on it: the status endpoint is polled
 * while a purchase settles (`refetchInterval`), and invoices are a slower-moving list that should
 * not be refetched on that cadence. Its own key also means the org-scope bridge evicts it
 * independently.
 */
export function useOrgInvoices(options?: { enabled?: boolean }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useQuery({
    queryKey: invoicesKey(orgId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<OrgInvoiceListResponse>(
        ORG_BILLING_INVOICES_ENDPOINT(orgId!),
        BILLING_REQUEST,
      );
      return data.invoices;
    },
    enabled: !!orgId && (options?.enabled ?? true),
    refetchOnMount: 'always',
  });
}

/**
 * Move a seat between tiers on its existing subscription, prorated.
 *
 * Unlike `useCreateCheckout` this does NOT return a URL and the browser does not leave: the change
 * is applied server-side against the live subscription and is complete when this resolves. That is
 * why it invalidates — there is no webhook round-trip to wait for, unlike a purchase.
 *
 * `retry: false` for the same reason as `useTopUp`: this charges a card (an upgrade invoices the
 * prorated difference immediately), and no Stripe idempotency key is sent, so an automatic retry
 * has a window in which it can bill twice.
 */
export function useChangePlan() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const invalidate = useBillingInvalidator();

  return useMutation({
    onMutate: () => captureOrgToken(),
    mutationFn: async (input: ChangePlanInput) => {
      const { data } = await axiosInstance.put<ChangePlanResponse>(
        ORG_BILLING_CHANGE_PLAN_ENDPOINT(orgId!, input.user_id),
        { plan: input.plan },
        payingRequest(),
      );
      return data;
    },
    // `alsoUsage`: a tier change moves the seat's LLM budget, which the Usage tab reads.
    onSuccess: (_data, _input, token) => invalidate(token, true),
    retry: false,
    meta: { silent: true },
  });
}

/**
 * One payment split across several seats.
 *
 * @deprecated Same as `useTopUp` — `/billing/topup/bulk` is slated for removal (backend #327). The
 * only mounted caller of `BulkTopUpDialog` is `assign` mode, which uses `useAssignCredits`.
 *
 * Shares `useTopUp`'s hazards and therefore its guards: `retry: false` because no Stripe
 * idempotency key is sent and a retried request has a window in which it can charge twice, and the
 * org token so a response arriving after an org switch cannot write into the new org's cache.
 *
 * Only the off-session branch invalidates. The Checkout branch has changed nothing yet — the
 * budgets move when Stripe's webhook confirms the payment, after the browser has been away.
 */
export function useBulkTopUp() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const invalidate = useBillingInvalidator();

  return useMutation({
    onMutate: () => captureOrgToken(),
    mutationFn: async (input: BulkTopUpInput) => {
      const { data } = await axiosInstance.post<BulkTopUpResponse>(
        ORG_BILLING_TOPUP_BULK_ENDPOINT(orgId!),
        input,
        payingRequest(),
      );
      return data;
    },
    onSuccess: (data, _input, token) => {
      if ('status' in data) invalidate(token, true);
    },
    retry: false,
    meta: { silent: true },
  });
}

/**
 * The org's credit balance and the ledger behind it.
 *
 * Polled the same way billing status is while a purchase settles — the balance only moves when
 * Stripe's webhook lands, so a purchase that has been accepted is not yet a balance that has grown.
 */
export function useOrgCredits(options?: { enabled?: boolean; refetchInterval?: number | false }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useQuery({
    queryKey: creditsKey(orgId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<OrgCreditsResponse>(
        ORG_BILLING_CREDITS_ENDPOINT(orgId!),
        BILLING_REQUEST,
      );
      return data;
    },
    enabled: !!orgId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? false,
    refetchOnMount: 'always',
  });
}

/**
 * Buy credit into the pool. Charges a card, so it carries the same guards as every other purchase:
 * `retry: false` (no Stripe idempotency key) and the org-token stale-write check.
 *
 * The balance does NOT move when this resolves — it moves when the webhook confirms.
 */
export function usePurchaseCredits() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const invalidate = useBillingInvalidator();

  return useMutation({
    onMutate: () => captureOrgToken(),
    mutationFn: async (input: PurchaseCreditsInput) => {
      const { data } = await axiosInstance.post<PurchaseCreditsResponse>(
        ORG_BILLING_CREDITS_PURCHASE_ENDPOINT(orgId!),
        input,
        payingRequest(),
      );
      return data;
    },
    onSuccess: (data, _input, token) => {
      if ('status' in data) invalidate(token, true);
    },
    retry: false,
    meta: { silent: true },
  });
}

/**
 * Assign pool credit to seats.
 *
 * Unlike every other mutation here this takes NO money — it spends credit already bought. That
 * makes it the one billing call that is safe to retry: the server refuses the whole assignment if
 * the balance cannot cover it and rolls back on any per-seat failure, so a repeat cannot
 * double-spend. `retry: false` is kept anyway for consistency with the rest of this file; the
 * safety here comes from the server, not from the client declining to retry.
 */
export function useAssignCredits() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const invalidate = useBillingInvalidator();

  return useMutation({
    onMutate: () => captureOrgToken(),
    mutationFn: async (input: AssignCreditsInput) => {
      const { data } = await axiosInstance.post<AssignCreditsResponse>(
        ORG_BILLING_CREDITS_ASSIGN_ENDPOINT(orgId!),
        input,
        BILLING_REQUEST,
      );
      return data;
    },
    onSuccess: (_data, _input, token) => invalidate(token, true),
    retry: false,
    meta: { silent: true },
  });
}
